/** @jsxImportSource solid-js */
import { createSignal, For, Show, type JSX } from "solid-js";
import { useWidgetStore } from "../hooks/widget-context";
import { popIn } from "../utils/widget-motion";
import { createPresence } from "./create-presence";
import { WidgetIcon } from "./widget-icon";

/** Collected-feedback list; rendered inside the island column above the CTA. */
export function WidgetReviewList(): JSX.Element {
  const store = useWidgetStore();
  const presence = createPresence(() =>
    store.reviewOpen() && store.collected().length > 0 ? store.collected() : null,
  );
  const [confirming, setConfirming] = createSignal(false);

  // Rows added while the list is already open pop in individually; the
  // initial batch (snapshotted by id at mount) rides the list's own entrance.
  let initialIds = new Set<string>();

  return (
    <Show when={presence.current()}>
      {(entries) => (
        <div
          ref={(el) => {
            presence.setEl(el);
            initialIds = new Set(entries().map((entry) => entry.id));
            setConfirming(false);
            popIn(el, { origin: "bottom right", from: "translateY(14px) scale(0.97)" });
          }}
          data-pl-review=""
          class="border-pl-border bg-pl-background text-pl-foreground shadow-pl box-border flex max-h-[40vh] w-[min(320px,calc(100vw-2.5rem))] [transform-origin:bottom_right] flex-col rounded-2xl border py-2 text-[13px]"
        >
          <div class="border-pl-border mb-1 flex items-center border-b px-3 pt-0.5 pb-2">
            <span class="text-pl-foreground text-sm font-semibold">
              Feedback ({entries().length})
            </span>
            <span class="flex-1" />
            <button
              type="button"
              data-pl-clear-all=""
              class="text-pl-destructive hover:bg-pl-destructive/10 cursor-pointer rounded-full border-0 bg-transparent px-2 py-1 font-[inherit] text-xs font-medium"
              onClick={() => store.clearCollected()}
            >
              Clear All
            </button>
            <button
              type="button"
              data-pl-collapse-list=""
              aria-label="Collapse list"
              title="Collapse list"
              class="text-pl-muted-foreground hover:bg-pl-foreground/[0.07] hover:text-pl-foreground -mr-1 grid h-6 w-6 cursor-pointer place-items-center rounded-full border-0 bg-transparent"
              onClick={() => store.closeReview()}
            >
              <WidgetIcon name="chevronDown" size={14} />
            </button>
          </div>
          <div class="min-h-0 flex-1 overflow-auto">
            <For each={entries()}>
              {(entry, index) => (
                <div
                  ref={(el) => {
                    if (initialIds.has(entry.id)) return;
                    popIn(el, { from: "translateY(6px) scale(0.97)" });
                    el.scrollIntoView({ block: "nearest" });
                  }}
                  class="group hover:bg-pl-foreground/[0.05] mx-1.5 flex items-center gap-1 rounded-lg px-1.5 py-1.5"
                >
                  <button
                    type="button"
                    data-pl-entry=""
                    class="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 border-0 bg-transparent py-1 text-left font-[inherit]"
                    title="Scroll to this feedback"
                    onClick={() => store.focusEntry(entry.id)}
                  >
                    <span class="bg-pl-gradient text-pl-primary-foreground grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-semibold">
                      {index() + 1}
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="text-pl-foreground block truncate text-sm leading-snug">
                        {entry.item.comment}
                      </span>
                      <span class="text-pl-muted-foreground block truncate font-mono text-xs leading-snug">
                        {entry.item.linkedElements[0].selector}
                        <Show when={entry.item.linkedElements.length > 1}>
                          {` +${entry.item.linkedElements.length - 1}`}
                        </Show>
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    data-pl-remove=""
                    aria-label="Remove"
                    class="text-pl-muted-foreground hover:text-pl-foreground focus:text-pl-foreground cursor-pointer border-none bg-transparent px-1 text-base leading-none opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 focus:opacity-100"
                    onClick={() => store.removeCollected(entry.id)}
                  >
                    ×
                  </button>
                </div>
              )}
            </For>
          </div>
          <div class="border-pl-border mt-1 border-t px-3 pt-2">
            <Show
              when={confirming()}
              fallback={
                <button
                  type="button"
                  data-pl-send-list=""
                  disabled={store.sendAllBusy()}
                  class="border-pl-primary/80 bg-pl-gradient text-pl-primary-foreground hover:bg-pl-gradient-hover active:bg-pl-gradient-active inline-flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 rounded-full border font-[inherit] text-[13px] font-medium shadow-[inset_0_1px_0_0_rgba(255,255,255,0.16)] disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:shrink-0"
                  onClick={() => setConfirming(true)}
                >
                  <WidgetIcon name="send" size={13} />
                  {store.sendAllBusy() ? "Sending…" : `Send all (${entries().length})`}
                </button>
              }
            >
              <div
                ref={(el) => popIn(el, { from: "translateY(4px)" })}
                data-pl-send-confirm=""
                class="flex items-center gap-2"
              >
                <span class="text-pl-muted-foreground min-w-0 flex-1 truncate text-xs">
                  Send {entries().length} feedback{entries().length === 1 ? "" : "s"}?
                </span>
                <button
                  type="button"
                  data-pl-cancel-send=""
                  class="text-pl-muted-foreground hover:bg-pl-foreground/[0.07] hover:text-pl-foreground inline-flex h-8 cursor-pointer items-center rounded-full border-0 bg-transparent px-3 font-[inherit] text-[13px] leading-none font-medium"
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-pl-confirm-send=""
                  disabled={store.sendAllBusy()}
                  class="border-pl-primary/80 bg-pl-gradient text-pl-primary-foreground hover:bg-pl-gradient-hover active:bg-pl-gradient-active inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3 font-[inherit] text-[13px] leading-none font-medium shadow-[inset_0_1px_0_0_rgba(255,255,255,0.16)] disabled:cursor-not-allowed disabled:opacity-45"
                  onClick={() => {
                    setConfirming(false);
                    void store.sendAllBatch();
                  }}
                >
                  Proceed
                </button>
              </div>
            </Show>
          </div>
        </div>
      )}
    </Show>
  );
}
